//frontend/src/components/checkout/GuestCheckout.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { 
    User, Mail, Phone, MapPin, AlertCircle, X, 
    LogIn, ArrowRight, ShoppingBag 
} from 'lucide-react';

const GuestCheckoutModal = ({ isOpen, onClose, onLogin, onContinueAsGuest }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-red-600">Checkout Options</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-6 h-6" />
                    </button>
                </div>
                
                <div className="space-y-4">
                    <button
                        onClick={onLogin}
                        className="w-full flex items-center justify-center gap-2 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                        <LogIn className="w-5 h-5" />
                        Login to Continue
                    </button>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-gray-500">OR</span>
                        </div>
                    </div>

                    <button
                        onClick={onContinueAsGuest}
                        className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                        <ShoppingBag className="w-5 h-5" />
                        Continue as Guest
                    </button>
                </div>

                <p className="mt-4 text-xs text-gray-500 text-center">
                    Your details will be saved for future orders
                </p>
            </div>
        </div>
    );
};

const GuestCheckoutForm = ({ onSubmit, isLoading }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        postalCode: '',
    });
    const [errors, setErrors] = useState({});

    const deliveryAreas = [
        { code: '562130', area: 'Gollarahatti' },
        { code: '562130', area: 'Kadabagere' },
        { code: '560112', area: 'Chikka Gollarahatti' },
        { code: '560091', area: 'Machohalli' },
        { code: '560091', area: 'Anjana Nagar' },
        { code: '560091', area: 'Byadarahalli' },
        { code: '562130', area: 'Tavarekere' },
        { code: '560091', area: 'Herohalli' },
        { code: '560091', area: 'Viswaneedam' },
        { code: '560091', area: 'Sunkadakatte' },
        { code: '560091', area: 'Muddayanapalya' },
        { code: '560091', area: 'Annapurneshwari Nagar' },
        { code: '560091', area: 'Kottigepalya' },
        { code: '560091', area: 'Sumanahalli' }
    ];

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone is required';
        } else if (!/^\d{10}$/.test(formData.phone)) {
            newErrors.phone = 'Please enter a valid 10-digit phone number';
        }
        if (!formData.address.trim()) newErrors.address = 'Address is required';
        if (!formData.postalCode) newErrors.postalCode = 'Please select a delivery area';
        
        // Email is optional, but validate format if provided
        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            onSubmit(formData);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <div
            style={{
                backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                backgroundRepeat: 'repeat',
                backgroundSize: 'auto',
                backgroundPosition: 'center'
            }}
            className="py-8"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl md:text-3xl font-bold text-red-600 mb-3">Guest Checkout</h1>
                        <p className="text-base text-gray-600">
                            Please fill in your delivery details below
                        </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-6 shadow-md">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Name Field */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <User className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={`block w-full pl-10 pr-3 py-2 border text-center ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-red-600 focus:border-red-600 text-sm`}
                                        placeholder="Enter your full name"
                                    />
                                </div>
                                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                            </div>

                            {/* Phone Field */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number *</label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Phone className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        maxLength="10"
                                        className={`block w-full pl-10 pr-3 py-2 border text-center ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-red-600 focus:border-red-600 text-sm`}
                                        placeholder="Enter 10-digit mobile number"
                                    />
                                </div>
                                {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
                            </div>

                            {/* Email Field (Optional) */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Email Address <span className="text-gray-500 text-xs">(Optional)</span>
                                </label>
                                <div className="mt-1 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={`block w-full pl-10 pr-3 py-2 border text-center ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-red-600 focus:border-red-600 text-sm`}
                                        placeholder="Enter your email address"
                                    />
                                </div>
                                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                            </div>

                            {/* Address Field */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Address *</label>
                                <div className="mt-1 relative">
                                    <div className="absolute top-3 left-3">
                                        <MapPin className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <textarea
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        rows="3"
                                        className={`block w-full pl-10 pr-3 py-2 border text-center ${errors.address ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-red-600 focus:border-red-600 text-sm`}
                                        placeholder="Enter your complete address"
                                    />
                                </div>
                                {errors.address && <p className="mt-1 text-xs text-red-600">{errors.address}</p>}
                            </div>

                            {/* Delivery Area Selection */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Delivery Area *</label>
                                <div className="mt-1">
                                    <select
                                        name="postalCode"
                                        value={formData.postalCode}
                                        onChange={handleChange}
                                        className={`block w-full pl-3 pr-10 py-2 border text-center ${errors.postalCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-red-600 focus:border-red-600 text-sm`}
                                    >
                                        <option value="">Select delivery area</option>
                                        {deliveryAreas.map(area => (
                                            <option key={area.code} value={area.code}>
                                                {area.area} - {area.code}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {errors.postalCode && <p className="mt-1 text-xs text-red-600">{errors.postalCode}</p>}
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <ArrowRight className="w-5 h-5 mr-2" />
                                        Continue to Payment
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { GuestCheckoutModal, GuestCheckoutForm };